import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAccount, useWriteContract, useSwitchChain } from 'wagmi';
import { CHAIN_TO_CHAIN_NAME, CHAIN_IDS_TO_MESSAGE_TRANSMITTER, SupportedChainId, EXPLORER_URLS } from '../../config/cctp/chains';
import { MESSAGE_TRANSMITTER_ABI, FEE_HOOK_ABI } from '../../config/cctp/contracts';
import { getFeeHookAddress, isFeeHookEnabled } from '../../config/cctp/feeHook';
import { getProgressPercentage, getRemainingTime } from '../../config/cctp/finality';
import { getEstimatedTime } from '../../config/cctp/fees';
import { CHAIN_IMAGES } from '../../config/chainImages';
import BridgeApiService from '../../services/bridgeApi';

const AVALANCHE_CHAIN_ID = SupportedChainId.AVALANCHE;

const TransactionHistory = ({ currentTransaction, onMintComplete }) => {
  const [transactions, setTransactions] = useState([]);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'pending'
  const [checkingTx, setCheckingTx] = useState(null);
  const [attestationStatuses, setAttestationStatuses] = useState({});
  const [activePolling, setActivePolling] = useState(null); // Track transaction being actively polled
  const pollingIntervalRef = useRef(null);
  const transactionsRef = useRef([]);
  const { address, chain } = useAccount();
  const { writeContractAsync } = useWriteContract();
  const { switchChainAsync } = useSwitchChain();

  // Update transactions ref whenever transactions change
  useEffect(() => {
    transactionsRef.current = transactions;
  }, [transactions]);

  // Track which transactions have been auto-minted
  const [autoMintedTxs, setAutoMintedTxs] = useState(new Set());
  const [autoMintInProgress, setAutoMintInProgress] = useState(null);

  // Define mintUSDC function before any useEffect that uses it
  const mintUSDC = useCallback(
    async (tx) => {
      if (!address) {
        console.error('Wallet not connected');
        return;
      }

      setCheckingTx(tx.id);

      // First check local state for attestation data
      let attestationData = attestationStatuses[tx.id];

      // If not in local state, fetch from database
      if (!attestationData || !attestationData.attestation || !attestationData.message) {
        console.log('Attestation not in local state, fetching from database...');
        try {
          const response = await BridgeApiService.checkAttestation(tx.burnHash);

          if (response.success && response.data && response.data.status === 'complete') {
            attestationData = {
              status: response.data.status,
              attestation: response.data.attestation,
              message: response.data.message,
            };

            // Store in local state for future use
            setAttestationStatuses((prev) => ({
              ...prev,
              [tx.id]: attestationData,
            }));
          } else {
            console.error('Attestation not ready in database');
            setCheckingTx(null);
            return;
          }
        } catch (error) {
          console.error('Failed to fetch attestation from database:', error);
          setCheckingTx(null);
          return;
        }
      }

      if (!attestationData || !attestationData.attestation || !attestationData.message) {
        console.error('No attestation data available for minting');
        setCheckingTx(null);
        return;
      }

      try {
        console.log('Starting mint process for transaction:', tx.id);

        // Get destination chain from transaction data
        const destinationChainId = tx.destinationChain || AVALANCHE_CHAIN_ID;

        // Switch to destination chain if needed
        if (chain?.id !== destinationChainId) {
          console.log(`Switching to destination chain (${destinationChainId})...`);
          try {
            await switchChainAsync({ chainId: destinationChainId });
            await new Promise((resolve) => setTimeout(resolve, 3000));
          } catch (switchError) {
            console.error('Chain switch failed:', switchError);
            throw switchError;
          }
        }

        // Check if fee hook is enabled for this chain
        const feeHookAddress = getFeeHookAddress(destinationChainId);
        const shouldUseFeeHook = isFeeHookEnabled(destinationChainId);

        // Execute mint transaction through fee hook if enabled, otherwise direct
        console.log(`Minting USDC ${shouldUseFeeHook ? 'via fee hook' : 'directly'}...`);

        const mintTx =
          shouldUseFeeHook && feeHookAddress
            ? await writeContractAsync({
                address: feeHookAddress,
                abi: FEE_HOOK_ABI,
                functionName: 'receiveMessageWithFee',
                args: [attestationData.message, attestationData.attestation, address], // address is the expectedRecipient
              })
            : await writeContractAsync({
                address: CHAIN_IDS_TO_MESSAGE_TRANSMITTER[destinationChainId],
                abi: MESSAGE_TRANSMITTER_ABI,
                functionName: 'receiveMessage',
                args: [attestationData.message, attestationData.attestation],
              });

        console.log('Mint transaction submitted:', mintTx);

        // Update mint transaction in database
        try {
          await BridgeApiService.updateMintTransaction(tx.burnHash, mintTx);
          console.log('Mint transaction stored in database');
        } catch (dbError) {
          console.error('Failed to update mint transaction in database:', dbError);
        }

        // Transaction is minting, will update on next API fetch

        // Use backend API to monitor mint transaction
        console.log('Monitoring mint transaction via backend API...');
        try {
          const monitorResponse = await BridgeApiService.monitorTransaction(mintTx, destinationChainId);

          if (monitorResponse.success && monitorResponse.data) {
            if (monitorResponse.data.status === 'confirmed') {
              if (monitorResponse.data.success) {
                console.log('Mint confirmed via API:', monitorResponse.data);

                // Transaction completed, will update on next API fetch

                // Mark as completed in database
                await BridgeApiService.completeTransaction(tx.burnHash, monitorResponse.data.blockNumber);
                console.log('Transaction marked as completed in database');

                // Notify parent component of successful mint
                if (onMintComplete) {
                  onMintComplete();
                }
              } else {
                throw new Error('Mint transaction failed on-chain');
              }
            } else if (monitorResponse.data.status === 'pending') {
              console.warn('Mint still pending after timeout');
              // Keep status as minting
            } else {
              throw new Error('Mint transaction not found on chain');
            }
          }
        } catch (monitorError) {
          console.error('Error monitoring mint transaction:', monitorError);
          // Error occurred, keeping status as ready_to_mint
        }

        // Clear attestation status after successful mint
        setAttestationStatuses((prev) => {
          const newStatuses = { ...prev };
          delete newStatuses[tx.id];
          return newStatuses;
        });
      } catch (error) {
        console.error('Error minting USDC:', error);
      } finally {
        setCheckingTx(null);
      }
    },
    [address, attestationStatuses, chain?.id, switchChainAsync, writeContractAsync, onMintComplete]
  );

  // Load transactions from database only
  useEffect(() => {
    const loadTransactions = async () => {
      if (!address) return;

      try {
        // Load all transactions
        const response = await BridgeApiService.getUserTransactions(address, {
          limit: 4,
          offset: 0,
        });

        // Load pending transactions separately (exclude both completed and failed)
        const pendingResponse = await BridgeApiService.getUserTransactions(address, {
          limit: 4,
          offset: 0,
          excludeStatus: 'completed,failed', // Exclude completed and failed transactions
        });

        console.log('Pending response:', pendingResponse);

        if (response.success && response.data) {
          // Transform database transactions to match local format
          const dbTransactions = response.data.map((tx) => ({
            id: tx._id || `${tx.burnTxHash}_${tx.createdAt}`,
            burnHash: tx.burnTxHash,
            mintHash: tx.mintTxHash,
            sourceChain: tx.sourceChain,
            destinationChain: tx.destinationChain,
            amount: tx.amount,
            status:
              tx.status === 'waiting_attestation'
                ? 'attestation_pending'
                : tx.status === 'ready_to_mint'
                ? 'ready_to_mint'
                : tx.status === 'completed'
                ? 'completed'
                : tx.status === 'failed'
                ? 'failed'
                : tx.status === 'minting'
                ? 'minting'
                : 'pending',
            timestamp: new Date(tx.createdAt).getTime(),
            burnTimestamp: new Date(tx.burnTimestamp || tx.createdAt).getTime(),
            isFastTransfer: tx.isFastTransfer || false,
            errorMessage: tx.errorMessage,
          }));

          setTransactions(dbTransactions);

          // Pre-load attestations for ready_to_mint transactions and trigger auto-mint
          const readyToMintTxs = dbTransactions.filter((tx) => tx.status === 'ready_to_mint');
          for (const tx of readyToMintTxs) {
            try {
              const response = await BridgeApiService.checkAttestation(tx.burnHash);
              if (response.success && response.data && response.data.status === 'complete') {
                setAttestationStatuses((prev) => ({
                  ...prev,
                  [tx.id]: {
                    status: response.data.status,
                    attestation: response.data.attestation,
                    message: response.data.message,
                  },
                }));
                
                // We'll trigger auto-mint after setting the attestation status
              }
            } catch (err) {
              console.error('Failed to pre-load attestation for tx:', tx.id, err);
            }
          }
        }

        // Process pending transactions
        if (pendingResponse.success && pendingResponse.data) {
          const dbPendingTransactions = pendingResponse.data.map((tx) => ({
            id: tx._id || `${tx.burnTxHash}_${tx.createdAt}`,
            burnHash: tx.burnTxHash,
            mintHash: tx.mintTxHash,
            sourceChain: tx.sourceChain,
            destinationChain: tx.destinationChain,
            amount: tx.amount,
            status:
              tx.status === 'waiting_attestation'
                ? 'attestation_pending'
                : tx.status === 'ready_to_mint'
                ? 'ready_to_mint'
                : tx.status === 'completed'
                ? 'completed'
                : tx.status === 'failed'
                ? 'failed'
                : tx.status === 'minting'
                ? 'minting'
                : 'pending',
            timestamp: new Date(tx.createdAt).getTime(),
            burnTimestamp: new Date(tx.burnTimestamp || tx.createdAt).getTime(),
            isFastTransfer: tx.isFastTransfer || false,
            errorMessage: tx.errorMessage,
          }));

          console.log('Setting pending transactions:', dbPendingTransactions);
          console.log(
            'Filtered statuses:',
            dbPendingTransactions.map((tx) => ({ id: tx.id, status: tx.status }))
          );
          setPendingTransactions(dbPendingTransactions);
        } else {
          // If no pending transactions, set empty array
          setPendingTransactions([]);
        }
      } catch (error) {
        console.error('Error loading transactions from database:', error);
        setTransactions([]);
      }
    };

    loadTransactions();
    // Reload transactions every 15 seconds to catch updates (less aggressive)
    const interval = setInterval(loadTransactions, 15000);
    return () => clearInterval(interval);
  }, [address]);

  // Auto-mint ANY transaction that's ready as soon as we see it
  useEffect(() => {
    // Check all transactions for any that are ready to mint (but exclude failed ones from auto-minting)
    const readyTx = transactions.find(tx =>
      (tx.status === 'ready_to_mint' ||
       (attestationStatuses[tx.id] && attestationStatuses[tx.id].status === 'complete')) &&
      !tx.mintHash &&
      tx.status !== 'failed' &&
      !autoMintedTxs.has(tx.burnHash) &&
      !autoMintInProgress
    );
    
    if (readyTx) {
      console.log('Found transaction ready to mint, auto-minting IMMEDIATELY:', readyTx.burnHash);
      setAutoMintInProgress(readyTx.burnHash);
      setAutoMintedTxs(prev => new Set([...prev, readyTx.burnHash]));
      
      // Execute mint without any delay
      mintUSDC(readyTx).finally(() => {
        setAutoMintInProgress(null);
      });
    }
  }, [transactions, attestationStatuses, autoMintedTxs, autoMintInProgress, mintUSDC]);

  // When a new transaction is created, start active polling for it
  useEffect(() => {
    if (currentTransaction && currentTransaction.burnHash) {
      // First, immediately refresh the transaction list
      const refreshList = async () => {
        if (!address) return;
        try {
          const response = await BridgeApiService.getUserTransactions(address, {
            limit: 4,
            offset: 0,
          });

          if (response.success && response.data) {
            const dbTransactions = response.data.map((tx) => ({
              id: tx._id || `${tx.burnTxHash}_${tx.createdAt}`,
              burnHash: tx.burnTxHash,
              mintHash: tx.mintTxHash,
              sourceChain: tx.sourceChain,
              destinationChain: tx.destinationChain,
              amount: tx.amount,
              status:
                tx.status === 'waiting_attestation'
                  ? 'attestation_pending'
                  : tx.status === 'ready_to_mint'
                  ? 'ready_to_mint'
                  : tx.status === 'completed'
                  ? 'completed'
                  : tx.status === 'failed'
                  ? 'failed'
                  : tx.status === 'minting'
                  ? 'minting'
                  : 'pending',
              timestamp: new Date(tx.createdAt).getTime(),
              burnTimestamp: new Date(tx.burnTimestamp || tx.createdAt).getTime(),
              isFastTransfer: tx.isFastTransfer || false,
              errorMessage: tx.errorMessage,
            }));

            setTransactions(dbTransactions);

            // Also update pending transactions list if needed
            const pendingTxs = dbTransactions.filter((tx) => tx.status !== 'completed' && tx.status !== 'failed');
            setPendingTransactions(pendingTxs);
          }
        } catch (error) {
          console.error('Error refreshing transaction list:', error);
        }
      };

      refreshList();

      // Start active polling for this specific transaction
      if (currentTransaction.status !== 'completed') {
        setActivePolling(currentTransaction.burnHash);
      }
    }
  }, [currentTransaction, address]);

  // Active polling for new transaction
  useEffect(() => {
    // Clear any existing interval first
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (activePolling && address) {
      const pollTransaction = async () => {
        try {
          // Poll the specific transaction (always checks for updates)
          const response = await BridgeApiService.getTransaction(activePolling);

          if (response.success && response.data) {
            const tx = response.data;

            // Update in our list
            const newStatus =
              tx.status === 'waiting_attestation'
                ? 'attestation_pending'
                : tx.status === 'ready_to_mint'
                ? 'ready_to_mint'
                : tx.status === 'completed'
                ? 'completed'
                : tx.status === 'failed'
                ? 'failed'
                : tx.status === 'minting'
                ? 'minting'
                : 'pending';

            // Update transactions state
            let readyToMintTx = null;
            
            setTransactions((prevTxs) => {
              const updated = prevTxs.map((t) =>
                t.burnHash === activePolling
                  ? {
                      ...t,
                      status: newStatus,
                      mintHash: tx.mintTxHash || t.mintHash,
                      errorMessage: tx.errorMessage,
                    }
                  : t
              );
              
              // Check if this transaction just became ready to mint
              if (newStatus === 'ready_to_mint' && tx.status === 'ready_to_mint') {
                readyToMintTx = updated.find(t => t.burnHash === activePolling);
              }
              
              return updated;
            });

            // Also update pending transactions
            setPendingTransactions((prevPending) => {
              const updated = prevPending.map((t) =>
                t.burnHash === activePolling
                  ? {
                      ...t,
                      status: newStatus,
                      mintHash: tx.mintTxHash || t.mintHash,
                      errorMessage: tx.errorMessage,
                    }
                  : t
              );
              // Filter out completed and failed transactions
              return updated.filter((t) => t.status !== 'completed' && t.status !== 'failed');
            });

            // Trigger auto-mint IMMEDIATELY if transaction is ready (but not if failed)
            if (readyToMintTx && readyToMintTx.status !== 'failed' && !autoMintedTxs.has(readyToMintTx.burnHash) && !autoMintInProgress) {
              console.log('Transaction ready to mint, auto-minting NOW:', readyToMintTx.burnHash);
              setAutoMintInProgress(readyToMintTx.burnHash);
              setAutoMintedTxs((prev) => new Set([...prev, readyToMintTx.burnHash]));

              // Mint immediately - no delay
              mintUSDC(readyToMintTx).finally(() => {
                setAutoMintInProgress(null);
              });
            }

            // Stop polling if completed
            if (tx.status === 'completed') {
              setActivePolling(null);
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
              }
              console.log('Transaction completed, stopping active polling');
            }
          }
        } catch (error) {
          console.error('Error polling transaction:', error);
        }
      };

      // Poll immediately
      pollTransaction();

      // Then poll every 2 seconds for faster response
      pollingIntervalRef.current = setInterval(pollTransaction, 2000);

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      };
    }
  }, [activePolling, address, autoMintedTxs, autoMintInProgress]);

  // This effect is now handled by the more comprehensive auto-mint effect above

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
    return date.toLocaleDateString();
  };

  const getProgressSteps = (tx) => {
    // Don't show steps for completed or failed transactions
    if (tx.status === 'completed' || tx.status === 'failed') {
      return null;
    }

    const steps = [
      {
        name: 'Approve USDC',
        completed: true, // If we have a transaction, approval is done
        active: false,
      },
      {
        name: 'Burn Transaction',
        completed: tx.burnHash ? true : false,
        active: tx.status === 'burning',
      },
      {
        name: 'Wait for Circle Attestation',
        completed: tx.status === 'ready_to_mint' || tx.status === 'minting' || tx.status === 'completed',
        active: tx.status === 'waiting_attestation' || tx.status === 'attestation_pending' || tx.status === 'pending',
      },
      {
        name: 'Mint USDC',
        completed: tx.status === 'completed',
        active: tx.status === 'ready_to_mint' || tx.status === 'minting',
      },
    ];

    return steps;
  };

  const getStatusBadge = (tx) => {
    // Check attestation status for additional context
    const attestationStatus = attestationStatuses[tx.id];

    const badges = {
      completed: { text: 'Completed', className: 'badge-success' },
      pending: { text: 'Pending', className: 'badge-warning' },
      attestation_pending: { text: 'Waiting', className: 'badge-info' },
      ready_to_mint: { text: 'Ready to Mint', className: 'badge-ready' },
      minting: {
        text: (
          <span className='minting-status'>
            <span className='minting-spinner'></span>
            Minting...
          </span>
        ),
        className: 'badge-minting',
      },
      failed: {
        text: tx.errorMessage ? <span title={tx.errorMessage}>Failed</span> : 'Failed',
        className: 'badge-error',
      },
    };

    // Override status text based on attestation polling result
    if (attestationStatus) {
      if (attestationStatus.status === 'complete' || (attestationStatus.status === 'pending_confirmations' && attestationStatus.attestation)) {
        return <span className='status-badge badge-ready'>Ready to Mint</span>;
      } else if (attestationStatus.status === 'pending_confirmations' && !attestationStatus.attestation) {
        return <span className='status-badge badge-info'>Confirming</span>;
      } else if (attestationStatus.status === 'pending') {
        return <span className='status-badge badge-info'>Still Waiting</span>;
      } else if (attestationStatus.status === 'error') {
        return <span className='status-badge badge-warning'>Check Failed</span>;
      }
    }

    const badge = badges[tx.status] || badges.pending;
    return <span className={`status-badge ${badge.className}`}>{badge.text}</span>;
  };

  const getExplorerUrl = (chainId, txHash) => {
    const explorerBase = EXPLORER_URLS[chainId];
    if (!explorerBase) return '#';
    return `${explorerBase}tx/${txHash}`;
  };

  // Removed unused checkAndCompleteMint function - functionality is in mintUSDC
  /*
  const checkAndCompleteMint = async (tx) => {
    if (!address) {
      console.error('Wallet not connected');
      return;
    }

    setCheckingTx(tx.id);

    try {
      console.log('Checking attestation for transaction:', tx.burnHash);

      // Check attestation through backend API (which caches results)
      const response = await BridgeApiService.checkAttestation(tx.burnHash);
      const attestationResponse =
        response.success && response.data
          ? {
              messages: [
                {
                  status: response.data.status,
                  attestation: response.data.attestation,
                  message: response.data.message,
                },
              ],
            }
          : null;

      if (attestationResponse && attestationResponse.messages && attestationResponse.messages.length > 0) {
        const message = attestationResponse.messages[0];

        if (message.status === 'complete' && message.attestation) {
          console.log('Attestation found! Preparing to mint...');

          // Get destination chain from transaction data
          const destinationChainId = tx.destinationChain || AVALANCHE_CHAIN_ID;

          // Switch to destination chain if needed
          console.log('Current chain before switch:', chain?.id, 'Chain name:', chain?.name);

          if (chain?.id !== destinationChainId) {
            console.log('Need to switch from', chain?.id, 'to', CHAIN_TO_CHAIN_NAME[destinationChainId], destinationChainId);

            try {
              // Request the chain switch
              await switchChainAsync({ chainId: destinationChainId });
              console.log('Chain switch requested, waiting for confirmation...');

              // Force a longer wait for chain switch to propagate
              await new Promise((resolve) => setTimeout(resolve, 3000));

              // Check if switch succeeded
              const currentChainHex = await window?.ethereum?.request({ method: 'eth_chainId' });
              const currentChainDecimal = parseInt(currentChainHex, 16);

              console.log('After switch - Chain from provider:', currentChainHex, '(decimal:', currentChainDecimal, ')');

              if (currentChainDecimal !== destinationChainId) {
                // If still not on destination chain, show error with manual instructions
                console.error(`Chain mismatch. Current: ${currentChainDecimal}, Expected: ${destinationChainId}`);
                throw new Error(`Please manually switch to ${CHAIN_TO_CHAIN_NAME[destinationChainId]} network and try again`);
              }
            } catch (switchError) {
              console.error('Chain switch failed:', switchError);
              console.error('Chain switch failed. Manual switch required.');
              throw switchError;
            }
          }

          console.log('Now on Avalanche, executing mint transaction...');

          // Double-check we're on the right chain before executing
          const finalChainCheck = await window?.ethereum?.request({ method: 'eth_chainId' });
          const finalChainDecimal = parseInt(finalChainCheck, 16);
          console.log('Final chain check before mint:', finalChainDecimal);

          if (finalChainDecimal !== AVALANCHE_CHAIN_ID) {
            throw new Error(`Still on wrong chain (${finalChainDecimal}). Please switch to Avalanche manually.`);
          }

          // Execute mint transaction
          const messageTransmitterAddress = CHAIN_IDS_TO_MESSAGE_TRANSMITTER[AVALANCHE_CHAIN_ID];
          console.log('Using MessageTransmitter on Avalanche:', messageTransmitterAddress);
          console.log('Message to mint:', message.message);
          console.log('Attestation:', message.attestation);

          const mintTx = await writeContractAsync({
            address: messageTransmitterAddress,
            abi: MESSAGE_TRANSMITTER_ABI,
            functionName: 'receiveMessage',
            args: [message.message, message.attestation],
          });

          console.log('Mint transaction submitted:', mintTx);

          // Transaction is minting, will update on next API fetch

          // Use backend API to monitor mint transaction
          console.log('Monitoring mint transaction via backend API...');
          try {
            const monitorResponse = await BridgeApiService.monitorTransaction(mintTx, AVALANCHE_CHAIN_ID);

            if (monitorResponse.success && monitorResponse.data) {
              if (monitorResponse.data.status === 'confirmed') {
                if (monitorResponse.data.success) {
                  console.log('Mint confirmed via API:', monitorResponse.data);

                  // Transaction completed, will update on next API fetch

                  // Update database
                  try {
                    await BridgeApiService.updateMintTransaction(tx.burnHash, mintTx);
                    await BridgeApiService.completeTransaction(tx.burnHash, monitorResponse.data.blockNumber);
                    console.log('Database updated with completion');
                  } catch (dbError) {
                    console.error('Failed to update database:', dbError);
                  }

                  console.log('Transfer completed successfully! USDC minted on Avalanche.');

                  // Notify parent component of successful mint
                  if (onMintComplete) {
                    onMintComplete();
                  }
                } else {
                  throw new Error('Mint transaction failed on-chain');
                }
              } else if (monitorResponse.data.status === 'pending') {
                console.warn('Mint still pending after timeout');
                // Keep as minting status
              } else {
                throw new Error('Mint transaction not found');
              }
            }
          } catch (monitorError) {
            console.error('Error monitoring mint:', monitorError);
            // Error occurred, keeping status for retry
          }
        } else {
          console.log(`Attestation not ready. Status: ${message.status || 'pending'}`);
        }
      } else {
        console.log('Attestation not found yet. Retry needed.');
      }
    } catch (error) {
      console.error('Error checking/completing mint:', error);
      console.error('Check and mint error:', error.message);
    } finally {
      setCheckingTx(null);
    }
  };
  */

  const displayTransactions = activeTab === 'pending' ? pendingTransactions : transactions;
  console.log('Active tab:', activeTab, 'Display transactions:', displayTransactions);

  if (displayTransactions.length === 0) {
    return (
      <div className='transaction-history'>
        <h3>Recent Transactions</h3>
        <div className='transaction-tabs'>
          <button className={`tab-button ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>
            All
          </button>
          <button className={`tab-button ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => setActiveTab('pending')}>
            Pending
          </button>
        </div>
        <div className='no-transactions'>
          <p>{activeTab === 'pending' ? 'No pending transactions' : 'No bridge transactions yet'}</p>
          <span className='hint'>{activeTab === 'pending' ? 'Pending transactions will appear here' : 'Your transaction history will appear here'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className='transaction-history'>
      <h3>Recent Transactions</h3>
      <div className='transaction-tabs'>
        <button className={`tab-button ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>
          All
        </button>
        <button className={`tab-button ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => setActiveTab('pending')}>
          Pending
        </button>
      </div>
      <div className='transactions-list'>
        {displayTransactions.map((tx) => (
          <div key={tx.id} className='transaction-item'>
            <div className='tx-header'>
              <span className='tx-route'>
                <div className='tx-chain'>
                  <img src={CHAIN_IMAGES[tx.sourceChain]} alt={CHAIN_TO_CHAIN_NAME[tx.sourceChain]} className='tx-chain-icon' />
                  <span>{CHAIN_TO_CHAIN_NAME[tx.sourceChain]}</span>
                </div>
                <span className='tx-arrow'>→</span>
                <div className='tx-chain'>
                  <img src={CHAIN_IMAGES[tx.destinationChain || AVALANCHE_CHAIN_ID]} alt={CHAIN_TO_CHAIN_NAME[tx.destinationChain || AVALANCHE_CHAIN_ID]} className='tx-chain-icon' />
                  <span>{CHAIN_TO_CHAIN_NAME[tx.destinationChain || AVALANCHE_CHAIN_ID]}</span>
                </div>
              </span>
              <span className='tx-time'>{formatDate(tx.timestamp)}</span>
            </div>
            <div className='tx-details'>
              <span className='tx-amount'>{tx.amount} USDC</span>
            </div>
            {/* Show progress bar for pending transactions (not failed) */}
            {(tx.status === 'pending' || tx.status === 'attestation_pending') && tx.status !== 'failed' && (
              <div className='tx-progress'>
                <div className='tx-progress-bar-container'>
                  <div
                    className='tx-progress-bar'
                    style={{
                      width: `${getProgressPercentage(tx.burnTimestamp || tx.timestamp, tx.sourceChain, tx.isFastTransfer ? 'fast' : 'typical')}%`,
                      transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />
                </div>
                <div className='tx-progress-text'>
                  <span className='tx-progress-time'>{getRemainingTime(tx.burnTimestamp || tx.timestamp, tx.sourceChain, tx.isFastTransfer ? 'fast' : 'typical')}</span>
                  <span className='tx-progress-estimate'>Est: {getEstimatedTime(tx.sourceChain, tx.isFastTransfer)}</span>
                </div>
              </div>
            )}
            <div className='tx-actions'>
              {tx.burnHash && (
                <a href={getExplorerUrl(tx.sourceChain, tx.burnHash)} target='_blank' rel='noopener noreferrer' className='tx-link-small'>
                  Burn TX ↗
                </a>
              )}
              {tx.mintHash && (
                <a href={getExplorerUrl(tx.destinationChain || AVALANCHE_CHAIN_ID, tx.mintHash)} target='_blank' rel='noopener noreferrer' className='tx-link-small'>
                  Mint TX ↗
                </a>
              )}
            </div>
            <div className='tx-footer'>
              <div className='tx-footer-left'>
                {getStatusBadge(tx)}
                {/* Show progress steps for uncompleted transactions */}
                {getProgressSteps(tx) && (
                  <div className='tx-progress-steps'>
                    {getProgressSteps(tx).map((step, index) => (
                      <div key={index} className={`progress-step ${step.active ? 'active' : ''} ${step.completed ? 'completed' : ''}`}>
                        <span className='step-icon'>{step.completed ? '✅' : step.active ? '⏳' : step.name === 'Mint USDC' ? <img src='/images/usdc.png' alt='USDC' className='usdc-step-icon' /> : '⭕'}</span>
                        <span className='step-name'>{step.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className='tx-footer-right'>
                {((!tx.mintHash && (tx.status === 'ready_to_mint' ||
                  (attestationStatuses[tx.id] && (attestationStatuses[tx.id].status === 'complete' ||
                  (attestationStatuses[tx.id].status === 'pending_confirmations' && attestationStatuses[tx.id].attestation))))) ||
                  (tx.status === 'failed' && tx.mintHash)) ? (
                    <button
                      className='mint-usdc-btn'
                      onClick={() => mintUSDC(tx)}
                      disabled={checkingTx === tx.id || tx.status === 'minting' || autoMintInProgress === tx.burnHash}
                      title={tx.status === 'failed' ? 'Retry minting USDC' :
                             autoMintInProgress === tx.burnHash ? 'Auto-minting in progress...' : 'Click to mint USDC'}
                    >
                      {checkingTx === tx.id || tx.status === 'minting' || autoMintInProgress === tx.burnHash ? (
                        <span className='btn-minting'>
                          <span className='btn-spinner'></span>
                          {autoMintInProgress === tx.burnHash ? 'Auto-Minting...' : 'Minting...'}
                        </span>
                      ) : (
                        tx.status === 'failed' ? 'Retry Mint' : 'Mint USDC'
                      )}
                    </button>
                  ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TransactionHistory;
